import * as React from "react";
import { useEffect } from "react";
import { PageProps } from "gatsby";
import * as styles from "./styles.module.scss";
import { StaticImage } from "gatsby-plugin-image";
import classNames from "classnames";
import { Breadcrumb } from "../../../components/breadcrumb/breadcrumb";
import Layout from "../../../components/layout/layout";
import Container from "../../../components/container/container";
import Heading from "../../../components/basic-components/heading/heading";
import { ProductBar } from "../../../components/product-bar/product-bar";
import { Level } from "react-accessible-headings";
import scrollTo from "gatsby-plugin-smoothscroll";
import { Helmet } from "react-helmet";
import blogImage from "../../../images/cozum/hamilelikte-oksuruk-nasil-gecer/Content-image.webp";
import { AnchorLink } from "gatsby-plugin-anchor-links";

const CoughPage: React.FC<PageProps> = ({ location }: PageProps) => {
  useEffect(() => {
    if (location.hash) {
      scrollTo(location.hash);
    }
  }, []);
  return (
    <Layout
      stickyHeader={false}
      seoConfig={{
        title: "Hamilelikte Öksürük Nasıl Geçer",
        description:
          "Hamilelikte öksürük nasıl geçer? Hamilelikte öksürük hakkında daha detaylı bilgiye sahip olmak için sayfamızı ziyaret edin!",
      }}
    >
      <Helmet>
        <script type="application/ld+json">
          {`
            {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {"@type": "ListItem","position": 1,"name": "Ana Sayfa","item": "https://www.bisolnatur.com.tr/"},
                  {"@type": "ListItem","position": 2,"name": "Öksürük","item": "https://www.bisolnatur.com.tr/oksuruk/"},
                  {"@type": "ListItem","position": 3,"name": "Hamilelikte Öksürük Nasıl Geçer","item": "${location.href}"}
                ]
            }
          `}
        </script>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "${location.href}"
              },
              "headline": "Hamilelikte Öksürük Nasıl Geçer",
              "image": "${location.origin + blogImage}",  
              "author": {
                "@type": "Organization",
                "name": "Bisolnatur",
                "url": "${location.origin}"
              },  
              "publisher": {
                "@type": "Organization",
                "name": "Bisolnatur",
                "logo": {
                  "@type": "ImageObject",
                  "url": "${location.origin}/logo.svg"
                }
              },
              "datePublished": "2022-01-27",
              "dateModified": "2022-01-27"
            }
          `}
        </script>
        <script type="application/ld+json">
          {`
            {"@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Öksürük Nedir?","acceptedAnswer":{"@type":"Answer","text":"Öksürük vücudun doğal savunma mekanizmalarından biridir ve en önemli amacı akciğerleri enfeksiyondan ve yabancı maddelerden korumaktır. Soluk borusu ve buradan akciğerlere doğru uzanan bronş ve bronşioller, yabancı maddelere karşı son derece duyarlıdır."}},{"@type":"Question","name":"Hamilelikte Öksürük Nasıl Geçer?","acceptedAnswer":{"@type":"Answer","text":"Solunum yolunda meydana gelen değişiklikler ve özellikle mukozal ödem ve sekresyonun artması, hamilelikte burun tıkanıklığı, burun kanaması, öksürük nöbetleri ve ses tonunda değişikliklere yol açabilir. Üst solunum yolunda görülen mukozal değişikliklerden bizzat östrojen hormonu sorumludur. Dolayısıyla gebelik dönemi boyunca bu mukozal değişiklikleri önlemek mümkün olmayacaktır. Ancak doğru bakım ve uygulamalarla ortaya çıkan semptomları kontrol altına almak mümkündür."}},{"@type":"Question","name":"Hamilelikte Öksürük Neden Olur?","acceptedAnswer":{"@type":"Answer","text":"Hamilelik dönemi vücutta anatomik, fizyolojik ve psikolojik değişikliklere yol açan kompleks bir zaman dilimidir. Bebeğin uterus (rahim) duvarına tutunması ile birlikte vücutta hızlı hormonal değişiklikler meydana gelir ve çok sayıda organın işleyişi bu değişikliklerden etkilenir."}},{"@type":"Question","name":"Hamilelikte Gıcık Öksürük Nasıl Geçer?","acceptedAnswer":{"@type":"Answer","text":"Hamilelik döneminde mukozal sekresyonun artışı şiddetli öksürük ataklarının başlıca nedenidir. Çoğunlukla doğru girişim ve uygulamalar sonucunda sekresyonun gerilemesi ile birlikte ataklar hafifler ve öksürük kontrol altına alınır. Ancak bazı durumlarda solunum yolundaki sekresyon giderilse dahi öksürük problemi devam edebilir. Bu durumda gelişen kuru, sekresyonsuz ve sıklıkla boğazda kaşıntı hissine yol açan öksürüğe gıcık öksürük adı verilir."}},{"@type":"Question","name":"Hamilelikte Öksürük Bebeğe Zarar Verir Mi?","acceptedAnswer":{"@type":"Answer","text":"Hamilelik sırasında yaşanan öksürük atakları çoğunlukla herhangi bir akciğer hastalığından bağımsız, mukozal değişikliklerden kaynaklı olarak ortaya çıkar. Bu tabloda öksürük problemi genellikle semptomatik tedavi ile geriler ve bebeğe herhangi bir zarar vermez."}}]}
          `}
        </script>
      </Helmet>
      <Breadcrumb
        pathname={location.pathname}
        noBg={true}
        className={styles.breadcrumb}
      />
      <Container fullWidth>
        <div className={classNames(styles.header)}>
          <StaticImage
            className={styles.image}
            src="../../../images/cozum/hamilelikte-oksuruk-nasil-gecer/Content-image.webp"
            alt="Boğazını tutan kadın görseli"
            width={1680}
            height={450}
          />
          <Container>
            <Heading>
              Hamilelikte Öksürük Nasıl
              <br /> Geçer
            </Heading>
            <ul>
              <li>
                <AnchorLink
                  className="blue"
                  to="#oksuruk-nedir"
                  title="Öksürük Nedir? "
                >
                  Öksürük Nedir?
                </AnchorLink>
              </li>
              <li>
                <AnchorLink
                  className="blue"
                  to="#hamilelikte-oksuruk-nasil-gecer"
                  title="Hamilelikte Öksürük Nasıl Geçer?"
                >
                  Hamilelikte Öksürük Nasıl Geçer?
                </AnchorLink>
              </li>
              <li>
                <AnchorLink
                  className="blue"
                  to="#hamilelikte-oksuruk-neden-olur"
                  title="Hamilelikte Öksürük Neden Olur?"
                >
                  Hamilelikte Öksürük Neden Olur?
                </AnchorLink>
              </li>
              <li>
                <AnchorLink
                  className="blue"
                  to="#hamilelikte-gicik-oksuruk-nasil-gecer"
                  title="Hamilelikte Gıcık Öksürük Nasıl Geçer?"
                >
                  Hamilelikte Gıcık Öksürük Nasıl Geçer?
                </AnchorLink>
              </li>
              <li>
                <AnchorLink
                  className="blue"
                  to="#hamilelikte-oksuruk-hangi-komplikasyonlara-neden-olur"
                  title="Hamilelikte Öksürük Hangi Komplikasyonlara Neden Olur? "
                >
                  Hamilelikte Öksürük Hangi Komplikasyonlara Neden Olur?
                </AnchorLink>
              </li>
              <li>
                <AnchorLink
                  className="blue"
                  to="#hamilelikte-oksuruk-bebege-zarar-verir-mi"
                  title="Hamilelikte Öksürük Bebeğe Zarar Verir Mi?"
                >
                  Hamilelikte Öksürük Bebeğe Zarar Verir Mi?
                </AnchorLink>
              </li>
            </ul>
          </Container>
        </div>
      </Container>
      <Container>
        <div className={styles.holder}>
          <p id="oksuruk-nedir" className="extra-bold">
            Soğuk algınlığı, grip, alerji ve solunum sistemini etkileyen daha
            pek çok hastalık, solunum sistemi mukozasının normalden çok daha
            fazla sekresyon salgılamasına yol açar. Burundan başlayıp
            akciğerlere kadar uzanan bu mukozal dokunun en önemli amaçlarından
            biri solunum yolu ile alınan virüs ve bakterileri tutmak ve bu
            salgılar vasıtası ile vücut dışına atmaktır. Burun, geniz ve
            gözlerde akıntı, hapşırma ve öksürme gibi reaksiyonlar genellikle bu
            sekresyon artışı ile ortaya çıkan durumlardır.
          </p>
          <section className={styles.kidsHolder}>
            <Level>
              <h2 className="orange underline">Öksürük Nedir?</h2>
              <p>
                Öksürük vücudun doğal savunma mekanizmalarından biridir ve en
                önemli amacı akciğerleri enfeksiyondan ve yabancı maddelerden
                korumaktır. Soluk borusu ve buradan akciğerlere doğru uzanan
                bronş ve bronşioller, yabancı maddelere karşı son derece
                duyarlıdır. Herhangi bir yabancı maddenin solunum yoluna girmesi
                ile sinir sistemi uyarılır, beyne acil iletiler gönderilir ve
                beyinde ilgili alanın uyarılması sonucu öksürük mekanizması
                başlatılır. Öksürük sırasında saatte yaklaşık 800 km hıza
                ulaşabilen bir hava akımı oluşur. Vücut bu şekilde solunum
                yoluna girmiş olan yabancı maddeyi vücuttan dışarı atmaya
                çalışır. Bu güçlü mekanizma sayesinde akciğerler boğulma ve
                yabancı cisim aspirasyonu gibi ölümcül olabilecek durumlara
                karşı korunmuş olur.
              </p>
              <p>
                Bunun yanı sıra enfeksiyon durumunda artan sekresyon da
                akciğerlerin havalanmasını engeller ve akciğer hasarına yol
                açabilir. Bu nedenle vücut, solunum yolunda artan sekresyonu
                yabancı bir cisim gibi algılar ve onu akciğerlerden
                uzaklaştırabilmek için sürekli olarak öksürük mekanizmasını
                devreye sokar.
              </p>
              <Level>
                <Heading id="hamilelikte-oksuruk-nasil-gecer">
                  Hamilelikte Öksürük Nasıl Geçer?
                </Heading>
              </Level>
              <p>
                Solunum yolunda meydana gelen değişiklikler ve özellikle mukozal
                ödem ve sekresyonun artması, hamilelikte burun tıkanıklığı,
                burun kanaması, öksürük nöbetleri ve ses tonunda değişikliklere
                yol açabilir. Üst solunum yolunda görülen mukozal
                değişikliklerden bizzat östrojen hormonu sorumludur. Dolayısıyla
                gebelik dönemi boyunca bu mukozal değişiklikleri önlemek mümkün
                olmayacaktır. Ancak doğru bakım ve uygulamalarla ortaya çıkan
                semptomları kontrol altına almak mümkündür.
              </p>
              <p>
                Hamilelikte öksürüğü geçirmeye yönelik başlıca uygulamalar şu
                şekilde sıralanabilir:
              </p>
              <ul>
                <li className="blue">
                  Solunum yolunda görülen herhangi bir tıkanıklık durumu kişinin
                  sağlıklı nefes alıp vermesini doğrudan engelleyen önemli bir
                  sorundur. Normal durumda tolere edilebilen burun
                  tıkanıklıkları, hamilelik döneminde solunum sıkıntısına yol
                  açabilir. Dolayısıyla her şeyden önce nazal mukoza bakımının
                  doğru şekilde yapılması ve burun tıkanıklıklarının önlenmesi
                  gerekir. Ödem arttığı için mukozal doku çok daha hassas hale
                  gelir ve burun kanaması gibi durumlara yol açabilir. Kanamalar
                  burunda tıkanıklık gelişme riskini artırdığı için burun içi
                  bakımı yaparken nazik olunmalı, nazal mukozaya uygun açıcı
                  spreyler tercih edilmelidir.
                </li>
                <li className="blue">
                  Öksürük ile birlikte boğaz tahrişi gelişebilir ve bu durum hem
                  öksürüğün daha da şiddetlenmesine yol açar hem de kişide yutma
                  zorluğu meydana gelir. Gebelik dönemi beslenme açısından
                  kritik dönemlerden biridir. Dolayısıyla bu dönemde öksürük
                  problemi yaşayan anne adaylarının boğaz tahrişine karşı
                  duyarlı olmaları gerekir. Boğaz tahrişi gelişmeden önce ılık
                  içecekler tüketilerek boğaz dokusunun yumuşaması sağlanabilir.
                  Ancak bu uygulamalar özellikle tekrarlayan öksürük atakları
                  yaşanması durumunda yetersiz kalır. Sık ve şiddetli öksürük
                  sırasında boğazda meydana gelen tahrişi önlemek için bu amaçla
                  geliştirilmiş pastil ve sprey gibi ürünleri tercih etmek
                  gerekir. Bu ürünlerin tercihi sırasında hamilelik dönemindeki
                  kadınların kullanımı için uygun içeriğe sahip olmasına dikkat
                  etmek ve mutlaka doktora danışarak kullanmak önemlidir.
                </li>
                <li className="blue">
                  Yeterli sıvı alımı gebelikte vücudun fizyolojik işleyişini
                  eksiksiz şekilde sürdürebilmesi için kritik önem taşıyan
                  noktalardan biridir. Vücut gebelikle birlikte oluşan yeni
                  düzene uyum sağlarken metabolik faaliyetlerin aksamadan yerine
                  getirilebilmesi için anne adaylarının gebeliğin başlangıcından
                  itibaren hekimleri tarafından aksi belirtilmediği sürece günde
                  en az 3 litre su tüketmesi gerekir. Yeterli sıvı alımı aynı
                  zamanda vücutta ödemin gerilemesine ve akciğer dokusunun
                  esnekliğinin korunmasına yardımcı olur. Özellikle kuru öksürük
                  şikayeti yaşayan anne adaylarının gebelik boyunca yeterli sıvı
                  alımına özen göstermesi son derece önemlidir.
                </li>
              </ul>
              <StaticImage
                className={styles.image}
                src="../../../images/cozum/hamilelikte-oksuruk-nasil-gecer/img.webp"
                alt="Hamile kadın görseli"
                width={792}
                height={526}
              />
            </Level>
          </section>
          <section
            id="hamilelikte-oksuruk-neden-olur"
            className={styles.kidsHolder}
          >
            <Level>
              <Level>
                <Heading>Hamilelikte Öksürük Neden Olur?</Heading>
              </Level>
              <p>
                Hamilelik dönemi vücutta anatomik, fizyolojik ve psikolojik
                değişikliklere yol açan kompleks bir zaman dilimidir. Bebeğin
                uterus (rahim) duvarına tutunması ile birlikte vücutta hızlı
                hormonal değişiklikler meydana gelir ve çok sayıda organın
                işleyişi bu değişikliklerden etkilenir.
              </p>
              <p>
                Gebeliğin ilk haftaları itibariyle solunum yolu mukozası
                normalden çok daha fazla kanlanmaya başlar. Akciğerlere
                gönderilen kan miktarı artar ve vücut hem bebek hem de anne için
                gerekli olan oksijeni temin etmeye çalışır. Solunum yolu
                mukozasının daha fazla kanlanmaya başlaması ile birlikte
                mukozada ödem gelişir, sekresyon salgısı artar ve mukozal doku
                hassasiyeti görülebilir. Bu gibi solunum yolu değişiklikleri
                gebeliğin başlangıcından itibaren ortaya çıkar ancak üçüncü
                trimester olarak adlandırılan 6-9 aylık gebelik döneminde artış
                gösterir. Sekresyonların artışı, mukoza dokusunun ödemlenmesi ve
                doku hassasiyeti gelişmesi gibi durumlar hamilelikte öksürük
                şikayetine yol açabilir. Vücut, öksürük mekanizmasını devreye
                sokarak solunum yolunu sekresyonlardan arındırmayı ve akciğeleri
                korumayı hedefler.
              </p>
              <p>
                Bu şikayetler özellikle altıncı gebelik ayından sonra belirgin
                hale gelir çünkü bu aylarda bebeğin büyümesi ve rahmin
                genişlemesi sonucunda akciğerlere yapılan basınç artar ve total
                akciğer kapasitesi azalır. Bu nedenle özellikle son üç aylık
                dönemde (üçüncü trimester) solunum ile ilgili değişiklikler daha
                şiddetli hissedilir.
              </p>
              <p>
                Hamilelikte özellikle kuru öksürüğe yol açan bir diğer neden ise
                reflü şikayetidir. Gebelik ile birlikte sindirim sistemi
                fizyolojisi de büyük oranda değişim yaşar ve mide asidinin
                artması sonucu reflü ve mide yanması gibi istenmeyen durumlar
                gelişebilir. Artmış mide asidi yemek borusuna doğru ilerler ve
                boğazda acı bir his yaratır. Bu durumda öksürük mekanizması
                uyarılır ve reflüye bağlı öksürük atakları yaşanabilir.
              </p>
            </Level>
          </section>
          <section
            id="hamilelikte-gicik-oksuruk-nasil-gecer"
            className={styles.kidsHolder}
          >
            <Level>
              <Level>
                <Heading>Hamilelikte Gıcık Öksürük Nasıl Geçer?</Heading>
              </Level>
              <p>
                Hamilelik döneminde mukozal sekresyonun artışı şiddetli öksürük
                ataklarının başlıca nedenidir. Çoğunlukla doğru girişim ve
                uygulamalar sonucunda sekresyonun gerilemesi ile birlikte
                ataklar hafifler ve öksürük kontrol altına alınır. Ancak bazı
                durumlarda solunum yolundaki sekresyon giderilse dahi öksürük
                problemi devam edebilir. Bu durumda gelişen kuru, sekresyonsuz
                ve sıklıkla boğazda kaşıntı hissine yol açan öksürüğe gıcık
                öksürük adı verilir.
              </p>
              <p>
                Genellikle boğazda tahriş nedeni ile ortaya çıkan gıcık öksürük
                için uygulanabilecek en iyi tedavi, boğazdaki tahrişin kontrol
                altına alınması veya dindirilmesi olacaktır. Ancak boğazdaki
                tahriş durumu her öksürük atağında artış göstereceği için bu
                dokuyu iyileştirmek sanıldığı gibi kolay olmayabilir. Öncelikle
                bu dönemde yumuşak ve ılık besinler tüketmek gerekir. Sebze
                çorbaları, ılık ballı süt ve hamilelik döneminde tüketilebilecek
                bitki çayları tercih edilebilir. Sık aralıklarla su içilmesi ve
                boğaz mukozasının sürekli olarak nemli tutulması yeni bir
                öksürük atağı durumunda gelişecek olan tahrişi önlemek adına
                önemlidir. Tüm bunların yanı sıra doğal içeriklerle
                geliştirilmiş boğaz spreyleri ve pastiller ile gıcık öksürüğe
                yol açan boğaz kuruluğu ve tahrişin hafifletilmesine yardımcı
                olunabilir.
              </p>

              <p>
                Hamilelik sırasında gıcık öksürük aynı zamanda reflüye bağlı
                olarak da ortaya çıkabilir. Mide asidinin yemek borusuna kaçışı
                boğazda yanma ve kaşıntı hissine yol açar ve bu durum reflüye
                bağlı öksürük ataklarının gelişmesine neden olur. Öksürüğün
                reflüden kaynaklı olarak ortaya çıktığı tablolarda öncelikle
                artmış mide asidini kontrol altına alacak tedaviler uygulanmalı
                ve reflü şikayeti ortadan kaldırılmalıdır. Daha sonra boğazda
                gelişen tahrişe yönelik iyileştirici yöntemler uygulanır ve
                tahrişe bağlı öksürük ataklarının gelişmesi önlenebilir.
              </p>
            </Level>
          </section>
          <section
            id="hamilelikte-oksuruk-hangi-komplikasyonlara-neden-olur"
            className={styles.kidsHolder}
          >
            <Level>
              <Level>
                <Heading>
                  Hamilelikte Öksürük Hangi Komplikasyonlara Neden Olur?
                </Heading>
              </Level>
              <p>
                Öksürük durumunda kişi genellikle birkaç saniye süren oksijen
                açlığı yaşar ve bu durum öksürüğün sonlanması ile birlikte
                ortadan kalkar. Ancak şiddetli ve uzun süren öksürük ataklarında
                bu durum normalden biraz daha yoğun hissedilebilir. Bunun
                dışında yaygın görülen öksürük komplikasyonları şu şekilde
                sıralanabilir:
              </p>
              <ul>
                <li className="blue">
                  Öksürük ile birlikte boğazda tahriş, ödem ve bazı durumlarda
                  kanama gibi şikayetler görülür. Bu durum hem öksürüğe bağlı
                  olarak gelişir hem de öksürük mekanizmasını tetikler.
                  Dolayısıyla öksürüğün etkin şekilde tedavi edilebilmesi için
                  bu komplikasyonun önlenmesi ve boğaz mukozasının korunması
                  gerekir.
                </li>
                <li className="blue">
                  Öksürük atakları sırasında akciğer dokusunun sık aralıklarla
                  ve şiddetli bir şekilde kasılıp gevşemesi göğüs duvarında ağrı
                  şikayetine yol açabilir.
                </li>
                <li className="blue">
                  Özellikle gebelik döneminde sağlıklı uyku düzenini korumak son
                  derece önemlidir. Ancak öksürük atakları genellikle gece
                  saatlerinde şiddetlenir ve uzun dönemde uyku düzenini ciddi
                  anlamda bozabilir. Bu nedenle özellikle gece yatmadan önce
                  boğaz mukozasını yumuşatarak kuru öksürük belirtilerini
                  kontrol altına almak ve öksürük dürtüsünü yatıştıracak tedavi
                  yöntemleri uygulamak gerekir.
                </li>
              </ul>
            </Level>
          </section>
          <section
            id="hamilelikte-oksuruk-bebege-zarar-verir-mi"
            className={styles.kidsHolder}
          >
            <Level>
              <Level>
                <Heading>Hamilelikte Öksürük Bebeğe Zarar Verir Mi?</Heading>
              </Level>
              <p>
                Hamilelik sırasında yaşanan öksürük atakları çoğunlukla herhangi
                bir akciğer hastalığından bağımsız, mukozal değişikliklerden
                kaynaklı olarak ortaya çıkar. Bu tabloda öksürük problemi
                genellikle semptomatik tedavi ile geriler ve bebeğe herhangi bir
                zarar vermez. Ancak astım tanısı almış hamile kadınlar için
                durum biraz daha farklı ilerleyebilir. Kronik bir akciğer
                rahatsızlığı olarak tanımlanan astım hastalığı durumunda öksürük
                yalnızca mukozal değişikliklerden kaynaklı olarak ortaya çıkmaz,
                akciğer dokusunda yaşanan daralma ve tıkanıklıklara bağlı olarak
                gelişir. Bu durum hem anne adayı hem de bebek için çeşitli risk
                unsurlarına yol açabilir. Dolayısıyla astım hastalığı bulunan
                kişilerin gebelik planlamasından doğuma kadar tüm süreçte uzman
                kontrolünde olması gerekir.
              </p>
            </Level>
          </section>
          <section className={styles.kidsHolder}>
            <Level>
              <Level>
                <Heading>Bisolnatur ile Öksürüğünü Rahatlat</Heading>
                <div className={classNames("flexbox", styles.productHolder)}>
                  <StaticImage
                    className={styles.image}
                    src="../../../images/bisolnatur-urun-yeni.png"
                    alt="Bisolnatur ürün görseli"
                    width={282}
                    height={282}
                  />
                  <div className={styles.content}>
                    <p>
                      Bisolnatur doğal içerikli öksürük şurubu, kuru ve balgamlı
                      öksürüğü rahatlatır. İçeriğinde bulunan bal sayesinde
                      Mukusun nemlenmesini destekleyerek fizyolojik olarak
                      atılmasını kolaylaştırır.Mukoza üzerinde bir “film”
                      oluşturarak tahriş eden dış etkenler ile temasını engelle
                    </p>
                    <p>
                      Kuru öksürük ataklarının büyük bir kısmı boğazdaki kuruluk
                      hissine bağlı olarak ortaya çıkar. Dolayısıyla kuruyan
                      boğaz mukozasını yumuşatarak kuru öksürük ataklarını
                      ortadan kaldırmak mümkündür. Bisolnatur öksürük şurubu
                      rahatlatıcı etkisinin yanı sıra boğaz mukozasında bulunan
                      duyu reseptörleri üzerinde koruyucu bir tabaka oluşturur.
                      İçeriğinde bulunan Poliflav M.A kompleksi sayesinde
                      antioksidan etkiye sahiptir.
                    </p>
                  </div>
                </div>
                <p>
                  Bisolnatur® gluten, koruyucu, renklendirici ve başka yapay
                  yardımcı maddeler içermez, doğal içeriklerle üretilmiştir.
                  Doğal şeftali, limon ve portakal aromaları ile tadı hoştur,
                  güvenle tüketilebilir. Siz de doktorunuza danışarak Bisolnatur
                  öksürük şurubunu doktorunuzun onayı ile tercih edebilir,
                  gebelik döneminde yaşanan öksürük ve boğaz ağrısı gibi
                  problemleri kolayca kontrol altına alabilirsiniz. Sağlıklı
                  günler dileriz.
                </p>
                <p>
                  <br />
                </p>
              </Level>
            </Level>
          </section>
          <section className={styles.kaynakca}>
            <Level>
              <Level>
                <Heading id="kaynakca">Kaynakça</Heading>
              </Level>
            </Level>
            <p>
              1- https://acikders.ankara.edu.tr/pluginfile.php/111499/mod_resource/content/0/Solunum%20Sistemi%20Hastalıkları.pdf
            </p>
            <p>
              2- https://toraks.org.tr/site/sf/wgmf/rfic/15125ff5f6335aaa991ff9c7eb9b3da31342596692d7d327f6fea00143831536.pdf
            </p>
            <p>
              3- https://turkthoracj.org/content/files/sayilar/151/buyuk/pdf_Toraksder_329.pdf
            </p>
          </section>
        </div>
        <div className="date">
          <p>Yayınlama Tarihi: 2022-01-27</p>
          <p>Son güncellenme Tarihi: 2022-01-27</p>
        </div>
      </Container>
      <ProductBar />
    </Layout>
  );
};
export default CoughPage;
